import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { MatRadioChange } from "@angular/material/radio";
import {
	HIGH_NET_INVESTOR_QUESTION_ONE,
	HIGH_NET_INVESTOR_QUESTION_TWO,
	RESTRICTED_INVESTOR_FUTURE_QUESTION_ONE,
	RESTRICTED_INVESTOR_PAST_QUESTION_ONE
} from "@helpers/constants";
import { QuestionnaireQuestion } from "@models/questionnaire_question";
import {
	CustomerSegmentationService,
	InputFieldStatus
} from "@modules/uk-finprom/components/customer-segmentation/customer-segmentation.service";
import { Subject, takeUntil } from "rxjs";

@Component({
	selector: "investor-questions",
	templateUrl: "./investor-questions.component.html",
	styleUrls: ["./investor-questions.component.scss"],
	standalone: false
})
export class InvestorQuestionsComponent implements OnInit, OnDestroy {
	private readonly restrictedInvestorLabel: string = "restricted_investor";

	public dataToUpdate: InputFieldStatus;
	public inputError: string;
	public minimumValue: number;
	public maximumValue: number;
	public disabled: boolean;
	private destroy$ = new Subject<void>();
	public answersWithYesLabels = [
		"uk_customer_segmentation_8_high_net_worth_investor_annual_income_yes_option",
		"uk_customer_segmentation_10_high_net_worth_investor_net_assets_yes_option",
		"uk_customer_segmentation_3_restricted_investor_past_twelve_invested_yes_option",
		"uk_customer_segmentation_5_restricted_investor_past_twelve_limit_investment_yes_option"
	];

	public answersWithNoLabels = [
		"uk_customer_segmentation_8_high_net_worth_investor_annual_income_no_option",
		"uk_customer_segmentation_10_high_net_worth_investor_net_assets_no_option",
		"uk_customer_segmentation_3_restricted_investor_past_twelve_invested_no_option",
		"uk_customer_segmentation_5_restricted_investor_past_twelve_limit_investment_no_option"
	];

	@Input() public question: QuestionnaireQuestion;

	constructor(private customerSegmentationService: CustomerSegmentationService) {}

	ngOnInit(): void {
		this.dataToUpdate = {
			inputFieldDisabled: false,
			inputFieldHasValue: false,
			noOptionSelected: false
		};

		this.minimumValue = this.getMinimumValue();
		this.maximumValue = this.getMaximumValue();

		this.question.getAnswers().forEach((a) => {
			if (this.answersWithYesLabels.includes(a.getLabel())) {
				a.setSelected(true);
			}
		});

		if (this.question.isInputField()) {
			const inputFieldId = Number(this.question.getId());
			this.customerSegmentationService.toggleInputField(inputFieldId, this.dataToUpdate);
		}

		this.customerSegmentationService.inputFieldStatus$.pipe(takeUntil(this.destroy$)).subscribe((responseData) => {
			if (this.question.isInputField()) {
				const questionId = this.question.getId();

				this.disabled = responseData[questionId]?.inputFieldDisabled;

				if (responseData[questionId]) {
					this.dataToUpdate = responseData[questionId];
				}

				if (responseData[questionId]?.inputFieldDisabled) {
					this.question.inputValue = "";
					this.inputError = "";
				}
			}
		});
	}

	public showHeader(): boolean {
		const labels = [
			HIGH_NET_INVESTOR_QUESTION_ONE,
			RESTRICTED_INVESTOR_PAST_QUESTION_ONE,
			RESTRICTED_INVESTOR_FUTURE_QUESTION_ONE
		];
		return labels.includes(this.question.getLabel());
	}

	public hasAdditionalText(): boolean {
		return this.question.getLabel() === HIGH_NET_INVESTOR_QUESTION_ONE;
	}

	public isRestrictedInvestor(): boolean {
		const labels = [RESTRICTED_INVESTOR_PAST_QUESTION_ONE, RESTRICTED_INVESTOR_FUTURE_QUESTION_ONE];
		return labels.includes(this.question.getLabel());
	}

	public onRadioChange(change: MatRadioChange): void {
		const radioBtn = change.source;
		const questionId = radioBtn.name;
		const answerId = radioBtn.value;
		const question = this.question.getId() === questionId;

		if (question) {
			this.question.getAnswers().forEach((a) => {
				return a.setSelected(a.getId() === answerId);
			});
		}

		const selectedAnswer = this.question.getAnswers().filter((a) => a.isSelected())[0];
		const yesAnswerSelected = this.answersWithYesLabels.includes(selectedAnswer.getLabel());
		const noAnswerSelected = this.answersWithNoLabels.includes(selectedAnswer.getLabel());
		const inputFieldId = Number(questionId) + 1;

		this.dataToUpdate = {
			...this.dataToUpdate,
			inputFieldDisabled: !yesAnswerSelected,
			noOptionSelected: noAnswerSelected
		};

		console.log(this.dataToUpdate);

		this.customerSegmentationService.toggleInputField(inputFieldId, this.dataToUpdate);
	}

	public onInputValueChange(event: Event): void {
		let { value } = event.target as HTMLInputElement;
		const inputFieldId = Number(this.question.getId());
		const inputHasValue = value.length > 0 && Number(value) >= this.minimumValue;

		this.dataToUpdate = {
			...this.dataToUpdate,
			inputFieldHasValue: inputHasValue
		};

		if (value && +value > this.maximumValue) {
			this.question.inputValue = this.maximumValue.toString();
		}

		if (this.question.getLabel().includes(this.restrictedInvestorLabel) && value.length > 2) {
			this.question.inputValue = this.maximumValue.toString();
		}

		this.customerSegmentationService.toggleInputField(inputFieldId, this.dataToUpdate);
	}

	public onInputFocusOut(event: FocusEvent): void {
		const { value } = event.target as HTMLInputElement;

		if (this.question.getLabel().includes(this.restrictedInvestorLabel)) {
			return;
		}

		this.inputError = Number(value) < this.minimumValue ? "checkout_minimum_is" : "";
	}

	public placeholderText(): string {
		if (this.question.isInputField()) {
			const questionLabel = this.question.getLabel();

			return questionLabel.includes(this.restrictedInvestorLabel)
				? `${questionLabel}_open.placeholder`
				: `${questionLabel}.placeholder`;
		}
	}

	public getMinimumValue(): number {
		let minimumValue: number = 250000;

		if (this.question.getLabel().includes(this.restrictedInvestorLabel)) {
			minimumValue = 1;
		} else if (this.question.getLabel() === HIGH_NET_INVESTOR_QUESTION_TWO) {
			minimumValue = 100000;
		}

		return minimumValue;
	}

	public getMaximumValue(): number {
		let maximum: number = 1000000000;

		if (this.question.getLabel().includes(this.restrictedInvestorLabel)) {
			maximum = 10;
		}

		return maximum;
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
